import { ExplanationOfBenefitModel } from '@ctw/shared/api/fhir/models/explanation-of-benefit';
import { SYSTEM_URLS } from '@zeus-health/types';

/**
 * Wraps a FHIR `ExplanationOfBenefit` for use as risk gap evidence.
 */
export class RiskDiagnosisModel extends ExplanationOfBenefitModel {
  /** @returns true if the risk diagnosis data is marked as eligible */
  public get riskEligible(): boolean {
    // Only Risk Diagnoses with explicit statuses are considered risk eligible.
    return this.riskAdjustmentStatus === 'eligible';
  }

  /** The label in the navigation drawer and the title in the clinical evidence card. */
  public get shortTitle() {
    return 'Risk Diagnoses';
  }

  public get claimType(): string | undefined {
    return this.resource.type?.coding?.[0]?.code || undefined;
  }

  public get providerDisplay(): string | undefined {
    return this.resource.provider?.display;
  }
  public get insurerDisplay(): string | undefined {
    return this.resource.insurer?.display;
  }

  /**
   * Reads the risk-adjustment eligibility status from the root extension the
   * risk-diagnoses import places on the `ExplanationOfBenefit`. Returns
   * `undefined` when the extension is absent (e.g. a non-risk-diagnoses EOB) or
   * has no recognized status value.
   */
  private get riskAdjustmentStatus(): RiskAdjustmentEligibilityStatus | undefined {
    const eligibilityExt = this.resource.extension?.find(
      (e) => e.url === SYSTEM_URLS.EXTENSIONS.RISK_DIAGNOSES.RISK_ADJUSTMENT_ELIGIBILITY,
    );
    const status = eligibilityExt?.extension?.find(
      (e) => e.url === SYSTEM_URLS.EXTENSIONS.RISK_DIAGNOSES.RISK_ADJUSTMENT_ELIGIBILITY_STATUS,
    )?.valueCode;
    return isRiskAdjustmentEligibilityStatus(status) ? status : undefined;
  }
}

// private functions copied from typescript-shared.
// future versions of the package will export these values/functions.

/** Verdict suffix rendered for each recognized risk-adjustment eligibility status. */
const RISK_ADJUSTMENT_ELIGIBILITY_VERDICTS = {
  eligible: 'risk-eligible',
  'not-eligible': 'not risk-eligible',
} as const;

/** All known/allowed values of the eligibility extension's `status` sub-extension. */
type RiskAdjustmentEligibilityStatus = keyof typeof RISK_ADJUSTMENT_ELIGIBILITY_VERDICTS;

function isRiskAdjustmentEligibilityStatus(
  value: unknown,
): value is RiskAdjustmentEligibilityStatus {
  // Own keys only, not `in`: `in` also matches inherited keys like "toString",
  // which would render the prototype member as the verdict.
  return (
    typeof value === 'string' && Object.keys(RISK_ADJUSTMENT_ELIGIBILITY_VERDICTS).includes(value)
  );
}

// to be added to  SYSTEM_URLS.PROFILE.RISK_DIAGNOSIS (currently not defined in typescript-shared)
export const RISK_DIAGNOSIS_PROFILE =
  'https://zusapi.com/fhir/StructureDefinition/risk/risk-diagnoses';
